import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faShieldAlt, faClock, faGem } from '@fortawesome/free-solid-svg-icons';
import ImageCarousel from '../common/ImageCarousel';
import { AnimatedSection } from '../common/AnimatedSection';

// Images du véhicule VTC
const vehicleImages = [
  {
    src: '/prius-ext.png',
    alt: 'Toyota Prius - Vue extérieure',
    title: 'Design Élégant',
    description: 'Véhicule moderne et respectueux de l\'environnement'
  },
  {
    src: '/prius-interior.png', 
    alt: 'Toyota Prius - Intérieur confortable',
    title: 'Confort Premium',
    description: 'Intérieur spacieux avec équipements haut de gamme'
  }
];

const AboutContainer = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: var(--background-color);
  
  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-sm);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-lg);
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }
  
  @media (max-width: 768px) {
    padding: 0;
    gap: var(--spacing-lg);
  }
`;

const AboutImageContainer = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid var(--accent);
    z-index: 1;
  }
  
  @media (max-width: 992px) {
    margin-bottom: var(--spacing-md);
    order: 1;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 768px) {
    max-width: none;
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    
    &:before {
      display: none;
    }
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

const MobileImage = styled.img`
  width: 100%;
  height: 280px;
  object-fit: cover;
  border-radius: 0;
  box-shadow: none;
  display: block;
  
  @media (max-width: 480px) {
    height: 240px;
  }
`;

const MobileImageWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: none;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const DesktopCarouselWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const AboutTextContent = styled.div`
  @media (max-width: 992px) {
    order: 2;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    text-align: left;
    padding: 0 1rem;
  }
`;

const AboutHeading = styled.h2`
  position: relative;
  margin-bottom: var(--spacing-md);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent);
  }
`;

const AboutSubheading = styled.h3`
  font-size: 1.25rem;
  color: var(--accent);
  margin-bottom: var(--spacing-sm);
  font-weight: 500;
`;

const AboutDescription = styled.p`
  margin-bottom: var(--spacing-md);
  line-height: 1.8;
`;

const ValuesList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const ValueContent = styled.div``;

const ValueTitle = styled.h4`
  margin-bottom: 5px;
  font-size: 1.1rem;
`;

const ValueDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  opacity: 0.9;
`;

const AboutSection: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <AboutContainer id="a-propos">
      <AboutContent>
        <AboutImageContainer>
          <DesktopCarouselWrapper>
            <ImageCarousel 
              images={vehicleImages}
              height="350px"
              autoRotate={true}
              rotationInterval={4000}
            />
          </DesktopCarouselWrapper>
          
          <MobileImageWrapper>
            <MobileImage 
              src={vehicleImages[0].src}
              alt={vehicleImages[0].alt}
            />
          </MobileImageWrapper>
        </AboutImageContainer>
        
        <AboutTextContent>
          <AnimatedSection>
            <AboutSubheading>À Propos</AboutSubheading>
            <AboutHeading>Votre Chauffeur Privé de Confiance</AboutHeading>
            <AboutDescription>
              Avec plus de 10 ans d'expérience dans le transport de personnes exigeantes, 
              je m'engage à offrir un service d'excellence qui dépasse vos attentes.
            </AboutDescription>
            <AboutDescription>
              Basé sur des valeurs de discrétion, ponctualité et élégance, mon service VTC est dédié 
              aux clients exigeants qui recherchent une expérience de transport personnalisée et raffinée.
            </AboutDescription>
          </AnimatedSection>
          
          <ValuesList>
            <ValueItem>
              <IconWrapper>
                <FontAwesomeIcon icon={faStar} />
              </IconWrapper>
              <ValueContent>
                <ValueTitle>Excellence</ValueTitle>
                <ValueDescription>
                  Un service irréprochable, minutieusement personnalisé pour répondre à vos besoins spécifiques.
                </ValueDescription>
              </ValueContent>
            </ValueItem>
            
            <ValueItem>
              <IconWrapper>
                <FontAwesomeIcon icon={faShieldAlt} />
              </IconWrapper>
              <ValueContent>
                <ValueTitle>Sécurité</ValueTitle>
                <ValueDescription>
                  Conduite professionnelle et véhicule moderne régulièrement entretenu pour votre sérénité.
                </ValueDescription>
              </ValueContent>
            </ValueItem>
            
            <ValueItem>
              <IconWrapper>
                <FontAwesomeIcon icon={faClock} />
              </IconWrapper>
              <ValueContent>
                <ValueTitle>Ponctualité</ValueTitle>
                <ValueDescription>
                  Arrivée en avance et gestion optimale des itinéraires pour respecter vos contraintes horaires.
                </ValueDescription>
              </ValueContent>
            </ValueItem>
            
            <ValueItem>
              <IconWrapper>
                <FontAwesomeIcon icon={faGem} />
              </IconWrapper>
              <ValueContent>
                <ValueTitle>Prestige</ValueTitle>
                <ValueDescription>
                  Une expérience premium dans un véhicule confortable avec des prestations soignées.
                </ValueDescription>
              </ValueContent>
            </ValueItem>
          </ValuesList>
        </AboutTextContent>
      </AboutContent>
    </AboutContainer>
  );
};

export default AboutSection; 